import React, { useState } from "react";
import { Button } from "@/components/Button";
import { DrawerPageProps } from "../types";
import { Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import { CartProduct } from "../../CartProduct";
import { RecommendedProductCardList } from "../../RecommendedProductCardList";
import { useCustomer } from "../../../CustomerContext";
import { productScentMapping } from "@/helpers/data/cartProductSwapVariants";
import { ProductSwapModule } from "../../ProductSwapModule";
import { MarketingIcon } from "@/components/icons";

import {
  ShopifyCartItem,
  useCartIsMutating,
  ShopifyCart,
  useRedirectToCheckout,
  shopifyCartApi,
  CartAddBodyWithTracking,
  cartSelect,
  CartAddLineItem,
  setCart,
} from "@/services/shopifyCart";
import { useBfState } from "@/helpers/useBfState";
import { PromoStepper } from "../../PromoStepper";
import { ShippingProgressBar } from "../../ShippingProgressBar";
import { Money } from "@/helpers/Money";
import { getFreeShippingBundleProductIds } from "@/helpers/constants";
import { useGlobalMetafields } from "@/services/globalMetafields";
import { CartDrawer, Page } from "..";
import { ProductSelectorPage } from "./ProductSelectorPage";
import {
  GiftProduct,
  giftWithPurchase,
} from "@/helpers/data/cartGiftProductsData";
import { DynamicBundleStepper } from "../../DynamicBundleStepper";
import { DroppsProduct, productSelect } from "@/store";
import { BundleIncludesDrawer } from "./BundleIncludesDrawer";
import { getEffectiveSubscriptionDiscountPercent } from "@/helpers/constants";

const { active: gwpActive, products: gwpProducts } = giftWithPurchase;

const calculateCompareAtPrice = (
  itemsWithOriginalShopifyProduct: (ShopifyCartItem & {
    shopifyProduct?: DroppsProduct;
  })[]
) => {
  return itemsWithOriginalShopifyProduct.reduce((totalCompareAtPrice, item) => {
    const sellingPlan = item.selling_plan_allocation;

    // If compare_at_price exists, use it; otherwise, fallback to price
    const compareAtPrice =
      sellingPlan && sellingPlan.compare_at_price
        ? sellingPlan.compare_at_price // Convert to numeric value
        : item.price; // Convert to numeric value

    // Multiply by the quantity and convert to dollars
    const compareAtPriceInDollars =
      (Number(compareAtPrice) * item.quantity) / 100;

    return totalCompareAtPrice + compareAtPriceInDollars;
  }, 0);
};

const calculateEffectiveCartSubtotal = (
  itemsWithOriginalShopifyProduct: (ShopifyCartItem & {
    shopifyProduct?: DroppsProduct;
  })[]
) => {
  return itemsWithOriginalShopifyProduct.reduce((total, item) => {
    // final_line_price includes all discounts applied to the line item
    // (subscription + Recharge tiered/Buy More Save More), which is what
    // cart subtotal in UI should reflect.
    const lineTotal =
      item.final_line_price ?? item.line_price ?? item.price.mul(item.quantity);
    return total.add(lineTotal);
  }, Money.zero);
};

/** Returns true if a product is the specific bundle eligible for free shipping. */
const isFreeShippingBundleProduct = (
  product?: DroppsProduct,
  globalMetafields?: any[]
): boolean => {
  if (!product) return false;
  const freeShippingBundleProductIds =
    getFreeShippingBundleProductIds(globalMetafields);

  // Check if this product ID is in the free shipping bundle list
  if (freeShippingBundleProductIds.includes(product.id)) {
    return true;
  }

  // Also check if this is a bundle product that contains the free shipping bundle items
  if (product.bundledItems && product.bundledItems.length > 0) {
    const bundledProductIds = product.bundledItems.map((item) => item.id);
    const hasAllFreeShippingItems = freeShippingBundleProductIds.every((id) =>
      bundledProductIds.includes(id)
    );
    return hasAllFreeShippingItems;
  }

  return false;
};

/**
 * Returns true when the cart contains both specific bundle products
 * (i.e., both products from the metafield are present).
 */
const cartContainsDecomposedBundle = (
  itemsWithOriginalShopifyProduct: (ShopifyCartItem & {
    shopifyProduct?: DroppsProduct;
  })[],
  allProductsMap: Map<number, DroppsProduct>,
  globalMetafields?: any[]
): boolean => {
  // Early return if no items in cart
  if (itemsWithOriginalShopifyProduct.length === 0) {
    return false;
  }

  const cartProductIdSet = new Set<number>(
    itemsWithOriginalShopifyProduct.map((item) => item.product_id)
  );

  // Get free shipping bundle product IDs from global metafield
  const freeShippingBundleProductIds =
    getFreeShippingBundleProductIds(globalMetafields);

  // If no bundle product IDs configured, return false (no free shipping)
  if (freeShippingBundleProductIds.length === 0) {
    return false;
  }

  // Check if both specific bundle products are present in the cart
  const allBundleProductsPresent = freeShippingBundleProductIds.every((id) =>
    cartProductIdSet.has(id)
  );

  return allBundleProductsPresent;
};

// TODO: updated code given test results
const dynamicDiscountTest = false;
const promoIds = gwpProducts.map((product) => product.variantId);
const CartItems = React.memo(function ProductItems({
  items,
  setOpenDrawer,
  allProductsMap,
}: {
  items: (ShopifyCartItem & { shopifyProduct?: DroppsProduct })[];
  setOpenDrawer: (kind: Page) => void;
  allProductsMap: Map<number, DroppsProduct>;
}) {
  const shouldShowUpgradeModule = (productId: number): boolean => {
    const ultrascentProduct = productScentMapping[productId];
    return ultrascentProduct !== undefined ? true : false;
  };

  return (
    <TransitionGroup className="p-1" component={"ul"}>
      {items.map((x, index) => (
        <Collapse key={x.key} component={"li"}>
          {index !== 0 && <hr className="divider-x mx-4 my-0" />}
          <CartProduct item={x} setOpenDrawer={setOpenDrawer} />
          {shouldShowUpgradeModule(x.product_id) && (
            <ProductSwapModule
              allProductsMap={allProductsMap}
              addedLineItem={x}
              type="upgrade"
              text={
                <>
                  <b>Ultrawash Plus</b> battles 24-hour stuck-on food with
                  powerful enzymes and a built-in rinse aid
                </>
              }
            />
          )}
          {x?.properties &&
            x.properties["_source-module"] === "cart_product_upgrade" && (
              <ProductSwapModule
                allProductsMap={allProductsMap}
                addedLineItem={x}
                type="downgrade"
                text={
                  <>
                    If you&apos;re not tackling the toughest messes, our regular{" "}
                    <b>Ultrawash</b> delivers a powerful everyday clean
                  </>
                }
              />
            )}
        </Collapse>
      ))}
    </TransitionGroup>
  );
});

const GwpPayload: CartAddBodyWithTracking = {
  items: [{ id: gwpProducts[0].variantId, quantity: 1 }],
  sourceModule: "cart_gift_with_purchase",
};

const AddPromoSection = ({
  cart,
  setOpenedDrawer,
}: {
  cart: ShopifyCart;
  setOpenedDrawer: (page: Page) => void;
}) => {
  const promoCartItem = selectPromoCartItem(cart);
  const addItem = shopifyCartApi.useTrackedAdd();

  const addGiftToCart = () => {
    addItem.mutate(GwpPayload);
  };

  if (!gwpActive || cart.items_subtotal_price.asDollars() < 30) {
    return null;
  }

  if (promoCartItem) {
    return null;
  }

  return (
    <div
      className={`flex items-center justify-between border-0 border-b border-t-2 border-solid border-b-primary-200 border-t-secondary bg-secondary-50 px-5 py-3`}
    >
      <div className="item-center mr-5 flex flex-col justify-center">
        <p className="m-0 text-sm font-semibold text-gray-700">
          You qualify for a FREE gift!
        </p>
        <p className="mt-1 text-sm text-gray-700">
          {giftWithPurchase.optionsActive
            ? "Choose your gift here."
            : "One (1) Kitchen Wand Brush"}
        </p>
      </div>
      <Button
        className="flex"
        size="md"
        onClick={
          giftWithPurchase.optionsActive
            ? () => {
                setOpenedDrawer({
                  kind: "choose-gift",
                });
              }
            : addGiftToCart
        }
      >
        Add
      </Button>
    </div>
  );
};

const selectPromoCartItem = (cart?: ShopifyCart) => {
  return cart && cart.items.find((x) => promoIds.includes(x.variant_id));
};

const CheckoutButton = ({
  cartItems,
  className,
}: {
  cartItems?: CartAddLineItem[];
  className?: string;
}) => {
  const [loading, setLoading] = useBfState(false);
  const redirectToCheckout = useRedirectToCheckout();

  return (
    <Button
      className={`flex w-full ${className}`}
      name="checkout"
      onClick={async () => {
        setLoading(true);

        if (cartItems) {
          await setCart(cartItems);
        }
        redirectToCheckout();
      }}
      size="lg"
      type="submit"
      loading={loading}
    >
      CHECKOUT
    </Button>
  );
};

const TopBar = ({
  cart,
  paidShippingThreshold,
}: {
  cart: ShopifyCart;
  paidShippingThreshold: number;
}) => {
  const eligibleItems: ShopifyCartItem[] = cart.items.filter(
    (x) => !x.title.toLocaleLowerCase().includes("bulk") && x.requires_shipping
  );

  if (eligibleItems.length === 0) {
    return null;
  }
  if (dynamicDiscountTest) {
    const ddItems = eligibleItems.filter(
      (x) => x.properties?.["_source-module"] !== "bundles_page"
    );
    return ddItems.length > 0 ? (
      <DynamicBundleStepper numItems={ddItems.length} />
    ) : null;
  }
  if (gwpActive) {
    return <PromoStepper cartValue={cart.items_subtotal_price} />;
  }

  return null;
  // return (
  //   <ShippingProgressBar
  //     cartValue={cart.items_subtotal_price}
  //     shippingThreshold={paidShippingThreshold}
  //   />
  // );
};

export interface OverviewPageProps extends DrawerPageProps {
  allProducts?: DroppsProduct[];
}

export const OverviewPage = ({ allProducts, ...props }: OverviewPageProps) => {
  const [openedDrawer, setOpenedDrawer] = useState<Page | undefined>(undefined);
  const isCartMutating = useCartIsMutating();
  const shopifyCart = shopifyCartApi.useFetch();
  const customer = useCustomer();
  const addItem = shopifyCartApi.useTrackedAdd();
  const cart = shopifyCart.data;
  const { data: globalMetafields } = useGlobalMetafields();

  const cartHasItems = allProducts && cart && cart.items.length > 0;
  const hasSubscriptionItem = cart && cartSelect.containsSubscription(cart);

  const paidShippingThreshold = Money.from(35, "$");

  // should memo?
  const productMap = new Map<number, DroppsProduct>(
    (allProducts ?? []).map((x) => [x.id, x])
  );

  const itemsWithOriginalShopifyProduct: (ShopifyCartItem & {
    shopifyProduct?: DroppsProduct;
  })[] = (cart?.items ?? []).map((item: ShopifyCartItem) => {
    const product = productMap.get(item.product_id);
    return product ? { ...item, shopifyProduct: product } : item;
  });
  // Check for any bundle (for decomposition logic)
  const containsBundle = itemsWithOriginalShopifyProduct.some(
    (x) => !!x.shopifyProduct?.bundledItems
  );

  // Check for specific free shipping bundle products
  const containsEligibleBundle = itemsWithOriginalShopifyProduct.some((x) =>
    isFreeShippingBundleProduct(x.shopifyProduct, globalMetafields)
  );

  // If user proceeded to checkout and the bundle decomposed into individual items,
  // detect presence of all bundle children to continue granting free shipping.
  const containsDecomposedBundle = cartContainsDecomposedBundle(
    itemsWithOriginalShopifyProduct,
    productMap,
    globalMetafields
  );

  const handleGiftSelect = (product: GiftProduct) => {
    const payload: CartAddBodyWithTracking = {
      items: [
        {
          id: product.variantId,
          quantity: 1,
        },
      ],
      sourceModule: "cart_gift_with_purchase",
    };
    addItem.mutate(payload);
  };

  // should memo?
  const unbundledCartItems = containsBundle
    ? itemsWithOriginalShopifyProduct.reduce<CartAddLineItem[]>((acc, item) => {
        if (
          item.shopifyProduct?.bundledItems &&
          item.shopifyProduct.tags.includes("bundle")
        ) {
          item.shopifyProduct.bundledItems.forEach((b) => {
            acc.push({
              id: productSelect.defaultVariant(b).id,
              quantity: item.quantity,
              properties: {
                ...item.properties,
                ...(item.shopifyProduct?.tags.includes("trial-product")
                  ? {
                      "_shipping-message":
                        "Full-size box ships 18 days after purchase",
                      "_renewal-message": "Renews every 4 months",
                    }
                  : {}),
              },
              selling_plan:
                (item.selling_plan_allocation?.selling_plan &&
                  b.sellingPlans.length > 0 &&
                  productSelect.defaultSellingPlan(b).id) ||
                undefined,
            });
          });
        } else {
          acc.push({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties,
            selling_plan: item.selling_plan_allocation?.selling_plan.id,
          });
        }
        return acc;
      }, [])
    : undefined;

  const effectiveCartSubtotal = calculateEffectiveCartSubtotal(
    itemsWithOriginalShopifyProduct
  );

  // Free shipping applies if subtotal meets threshold or bundle (original or decomposed) is present
  const qualifiesForFreeShipping =
    containsEligibleBundle ||
    containsDecomposedBundle ||
    !(effectiveCartSubtotal < paidShippingThreshold);

  return (
    <>
      <div
        className="flex flex-col"
        style={{ minHeight: "calc(100% - 64px)" }}
        id={cartHasItems ? undefined : "empty-cart"}
      >
        {cartHasItems ? (
          <>
            {
              <div
                className={`sticky left-0 top-16 z-20 border-0 border-b border-solid border-primary-200 bg-white px-5 py-4`}
              >
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3 space-y-1">
                    <span
                      aria-busy={isCartMutating}
                      className="body block text-lg font-semibold transition-colors aria-busy:text-gray-700/50"
                    >{`Subtotal (${cart.item_count} ${
                      cart.item_count <= 1 ? "item" : "items"
                    })`}</span>
                  </div>
                  <span
                    aria-busy={isCartMutating}
                    className="body text-right text-lg font-semibold transition-colors aria-busy:text-gray-700/50"
                  >
                    {effectiveCartSubtotal.format()}
                  </span>

                  <>
                    <div className="col-span-full leading-none">
                      {qualifiesForFreeShipping ? (
                        <span className="body text-xs">
                          Your order ships{" "}
                          <span className="font-semibold">FREE</span>!
                        </span>
                      ) : (
                        <span className="body text-xs">
                          You&#39;re{" "}
                          <span className="font-semibold text-secondary-700">
                            {paidShippingThreshold
                              .sub(effectiveCartSubtotal)
                              .format()}
                          </span>{" "}
                          away from <span className="font-semibold">FREE</span>{" "}
                          shipping!
                        </span>
                      )}
                    </div>

                    <div className="col-span-full">
                      <ShippingProgressBar
                        cartValue={effectiveCartSubtotal}
                        shippingThreshold={
                          qualifiesForFreeShipping
                            ? 0
                            : paidShippingThreshold.asDollars()
                        }
                        returnTestVariant={true}
                      />
                    </div>
                  </>
                </div>
                <div className="mt-3 grid gap-2">
                  <CheckoutButton
                    cartItems={unbundledCartItems}
                    className="bg-orange-300 text-primary-800 not-disabled:hover:bg-orange-400 not-disabled:hover:text-primary-900"
                  />
                  <p className="body text-center text-xs">
                    {qualifiesForFreeShipping ||
                    !(effectiveCartSubtotal < paidShippingThreshold)
                      ? "Tax calculated in checkout"
                      : "Shipping & tax calculated in checkout"}
                  </p>
                </div>
              </div>
            }

            <CartItems
              items={itemsWithOriginalShopifyProduct}
              setOpenDrawer={setOpenedDrawer}
              allProductsMap={productMap}
            />
            {gwpActive && (
              <AddPromoSection cart={cart} setOpenedDrawer={setOpenedDrawer} />
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-2 px-5 py-10">
            <span className="block text-2xl font-semibold text-primary">
              Your Cart
            </span>
            <span>...is currently empty.</span>
          </div>
        )}
        <div className="w-full grow bg-primary-50 pb-4">
          <RecommendedProductCardList
            showShopNow={!cartHasItems}
            headline={
              cartHasItems
                ? "Inspired by your cart"
                : "Not Sure Where To Start?"
            }
            subHeadline={cartHasItems ? undefined : "Try Our Best Sellers"}
            cardsShown={2}
          />
          {!cartHasItems && (
            <div className="m-4">
              <Button
                className="flex"
                href="/collections/all-products"
                component="a"
              >
                SHOP ALL PRODUCTS
              </Button>
            </div>
          )}
        </div>
      </div>
      <CartDrawer
        open={openedDrawer?.kind === "choose-gift"}
        title={"Free Gift"}
        contents={(nav) =>
          openedDrawer !== undefined ? (
            <ProductSelectorPage {...nav} onSelect={handleGiftSelect} />
          ) : null
        }
        {...props}
        onBack={() => setOpenedDrawer(undefined)}
        transparentBackdrop={true}
      />
      <CartDrawer
        open={openedDrawer?.kind === "bundle-drawer"}
        title={"What's Included"}
        contents={(nav) =>
          openedDrawer?.kind === "bundle-drawer" ? (
            <BundleIncludesDrawer
              {...nav}
              bundledProducts={openedDrawer.bundledProducts}
              item={openedDrawer.item}
            />
          ) : null
        }
        {...props}
        onBack={() => setOpenedDrawer(undefined)}
        transparentBackdrop={true}
      />
    </>
  );
};
